// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import jsQR from "jsqr";

interface Props {
  onScan: (value: string) => void;
  onClose: () => void;
  label?: string;
}

export function QrScanner({ onScan, onClose, label = "Point camera at QR code" }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");

  useEffect(() => {
    start();
    return () => stop();
  }, []);

  async function start() {
    if (!window.isSecureContext) {
      setError("Camera requires HTTPS. Type the value below.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera API not supported in this browser. Type the value below.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setScanning(true);
        rafRef.current = requestAnimationFrame(scan);
      }
    } catch (e: any) {
      if (e.name === "NotAllowedError") {
        setError("Camera permission denied. Tap the lock icon in your browser address bar to reset permissions, then try again.");
      } else if (e.name === "NotFoundError") {
        setError("No camera found on this device. Type the value below.");
      } else if (e.name === "NotSupportedError" || !window.isSecureContext) {
        setError("Camera requires a secure connection (HTTPS). Type the value below.");
      } else {
        setError(`Camera unavailable (${e.name || e.message}). Type the value below.`);
      }
    }
  }

  function stop() {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }

  function scan() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    let result: string | null = null;

    // Try native BarcodeDetector first (Chrome/Android)
    if ("BarcodeDetector" in window) {
      try {
        const detector = new (window as any).BarcodeDetector({
          formats: ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "data_matrix"]
        });
        detector.detect(canvas).then((codes: any[]) => {
          if (codes.length > 0) {
            stop();
            onScan(codes[0].rawValue);
          } else {
            rafRef.current = requestAnimationFrame(scan);
          }
        }).catch(() => {
          rafRef.current = requestAnimationFrame(scan);
        });
        return;
      } catch {}
    }

    // Fallback: jsQR (bundled)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert"
    });
    if (code?.data) {
      stop();
      onScan(code.data);
      return;
    }
    rafRef.current = requestAnimationFrame(scan);
  }

  function handleManual(e: React.FormEvent) {
    e.preventDefault();
    if (manual.trim()) {
      stop();
      onScan(manual.trim());
    }
  }

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-dialog">
        <div className="qr-scanner-header">
          <span>{label}</span>
          <button onClick={() => { stop(); onClose(); }}><X size={18} /></button>
        </div>
        {!error && (
          <div className="qr-scanner-viewfinder">
            <video ref={videoRef} autoPlay playsInline muted className="qr-scanner-video" />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <div className="qr-scanner-reticle">
              <div className="qr-scanner-corner tl" /><div className="qr-scanner-corner tr" />
              <div className="qr-scanner-corner bl" /><div className="qr-scanner-corner br" />
            </div>
            {scanning && <p className="qr-scanner-hint">Scanning…</p>}
          </div>
        )}
        {error && <p className="qr-scanner-error">{error}</p>}
        <form className="qr-scanner-manual" onSubmit={handleManual}>
          <input
            type="text"
            placeholder="Or type / paste the code value here"
            value={manual}
            onChange={e => setManual(e.target.value)}
            autoFocus={!!error}
          />
          <button type="submit" disabled={!manual.trim()}>
            <Camera size={14} /> Use value
          </button>
        </form>
      </div>
    </div>
  );
}
